import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  LogIn,
  UserCircle,
  Pencil,
  ChevronRight,
  LayoutGrid,
  Mail,
  Lock,
  ChevronDown,
  ChevronUp,
  Eye,
  EyeOff,
  Loader2,
  AlertCircle,
  CheckCircle2,
  KeyRound,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import RoleBadge from '../components/RoleBadge';
import PhotoProfil from '../components/PhotoProfil';
import ProfilBenevole from '../components/ProfilBenevole';
import MesFacturesSponsor from '../components/MesFacturesSponsor';
import DoubleFacteur from '../components/DoubleFacteur';
import MesClesSecurite from '../components/MesClesSecurite';
import MesDevisSponsor from '../components/MesDevisSponsor';
import MonSiteSponsor from '../components/MonSiteSponsor';
import { changerEmail, changerMotDePasse, fetchBenevoleMoi, modifierProfil } from '../services/api';

export default function MonEspace() {
  const { user, isLoading, logout } = useAuth();
  const queryClient = useQueryClient();

  // La fiche bénévole porte la date de naissance : la même requête que celle
  // du bloc plus bas, donc servie par le cache.
  const benevoleQuery = useQuery({ queryKey: ['benevole-moi'], queryFn: fetchBenevoleMoi });

  const [formEmail, setFormEmail] = useState({ email: '', currentPassword: '' });
  const [formMdp, setFormMdp] = useState({
    currentPassword: '',
    newPassword: '',
    newPasswordConfirmation: '',
  });
  const [formProfil, setFormProfil] = useState({ telephone: '', anonyme: false, newsletter: false });
  // Le formulaire partait vide : enregistrer sans rien saisir effaçait le
  // téléphone et remettait la visibilité à zéro. Il porte désormais les
  // valeurs du profil dès qu'elles sont connues.
  useEffect(() => {
    if (!user) return;
    setFormProfil({
      telephone: user.telephone ?? '',
      anonyme: user.anonyme === true,
      newsletter: user.newsletter === true,
    });
  }, [user]);

  // La fiche s'ouvre en lecture : le formulaire ne se deplie qu'a la
  // demande, l'ecran servant d'abord a se reconnaitre.
  const [edition, setEdition] = useState(false);
  // Changer d'e-mail ou de mot de passe reste rare : les deux cartes se
  // reduisent a leur titre et ne se deplient qu'au bouton.
  const [ouvertEmail, setOuvertEmail] = useState(false);
  const [ouvertMdp, setOuvertMdp] = useState(false);
  const [visible, setVisible] = useState(false);
  const [notice, setNotice] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const mutationEmail = useMutation({
    mutationFn: () => changerEmail(formEmail.email, formEmail.currentPassword),
    onSuccess: (d) => {
      setNotice({ type: 'success', message: d.message });
      setFormEmail({ email: '', currentPassword: '' });
      queryClient.invalidateQueries({ queryKey: ['me'] });
    },
    onError: (e: Error) => setNotice({ type: 'error', message: e.message }),
  });

  const mutationMdp = useMutation({
    mutationFn: () =>
      changerMotDePasse(
        formMdp.currentPassword,
        formMdp.newPassword,
        formMdp.newPasswordConfirmation,
      ),
    onSuccess: (d) => {
      setNotice({ type: 'success', message: d.message });
      setFormMdp({ currentPassword: '', newPassword: '', newPasswordConfirmation: '' });
    },
    onError: (e: Error) => setNotice({ type: 'error', message: e.message }),
  });

  const mutationProfil = useMutation({
    mutationFn: () =>
      modifierProfil({
        telephone: formProfil.telephone.trim() || null,
        anonyme: formProfil.anonyme,
        newsletter: formProfil.newsletter,
      }),
    onSuccess: (d) => {
      setNotice({ type: 'success', message: d.message });
      queryClient.invalidateQueries({ queryKey: ['me'] });
    },
    onError: (e: Error) => setNotice({ type: 'error', message: e.message }),
  });

  if (isLoading) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="p-6 lg:p-10 text-center space-y-4 pb-24 lg:pb-0 max-w-md mx-auto">
        <UserCircle className="w-16 h-16 mx-auto text-primary-200" />
        <h1 className="text-2xl lg:text-3xl font-bold">Informations</h1>
        <p className="text-gray-600 dark:text-gray-400">
          Connectez-vous pour accéder à vos devis, factures et planning bénévole.
        </p>
        <Link to="/login" className="btn-primary w-full">
          <LogIn className="w-5 h-5" />
          Se connecter
        </Link>
        <Link to="/mot-de-passe-oublie" className="btn-outline w-full">
          <KeyRound className="w-5 h-5" />
          Mot de passe oublié
        </Link>
      </div>
    );
  }

  // L'âge, s'il se déduit. La fiche bénévole porte la date de naissance ;
  // elle est déjà en cache le plus souvent, la requête ne coûte donc rien de
  // visible.
  const age = (() => {
    const iso = benevoleQuery.data?.benevole?.dateNaissance;
    if (typeof iso !== 'string' || iso === '') return null;
    const naissance = new Date(iso);
    if (Number.isNaN(naissance.getTime())) return null;
    const aujourdhui = new Date();
    let ans = aujourdhui.getFullYear() - naissance.getFullYear();
    // Le mois et le jour comptent : sans eux, quelqu'un né en décembre
    // gagnerait un an dès le 1er janvier.
    const mois = aujourdhui.getMonth() - naissance.getMonth();
    if (mois < 0 || (mois === 0 && aujourdhui.getDate() < naissance.getDate())) ans--;
    return ans >= 0 && ans < 130 ? ans : null;
  })();

  const estBenevole =
    user.roles?.includes('ROLE_VOLUNTEER') === true ||
    user.roles?.includes('ROLE_BUREAU') === true ||
    user.roles?.includes('ROLE_ADMIN') === true;

  const emailInvalide = !formEmail.email.includes('@') || !formEmail.currentPassword;
  const mdpInvalide =
    !formMdp.currentPassword ||
    formMdp.newPassword.length < 8 ||
    formMdp.newPassword !== formMdp.newPasswordConfirmation;

  return (
    <div className="p-4 lg:p-0 space-y-5 lg:space-y-6 pb-24 lg:pb-0">
      <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100">Informations</h1>

      <div className="card lg:p-6 space-y-4">
        {/* Repos : photo, nom, e-mail, téléphone. Rien d'autre — l'écran
            s'ouvre sur qui l'on est, pas sur un formulaire. */}
        <div className="flex items-start gap-3">
          {edition ? (
            <PhotoProfil
              photoUrl={user.photoUrl}
              nom={user.fullName || user.email}
              onNotice={setNotice}
            />
          ) : (
            <>
              <div className="w-16 h-16 rounded-full overflow-hidden bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-primary-700 dark:text-primary-300 font-bold text-xl shrink-0">
                {user.photoUrl ? (
                  <img
                    src={user.photoUrl}
                    alt={user.fullName || user.email}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  (user.fullName || user.email).charAt(0).toUpperCase()
                )}
              </div>

              <div className="min-w-0 flex-1">
                <p className="font-semibold lg:text-lg truncate text-gray-900 dark:text-gray-100">
                  {user.fullName || user.email}
                  {/* L'âge en exposant, et seulement s'il est connu : il sert
                      à savoir d'un coup d'œil si l'on a affaire à un mineur —
                      autorisation parentale, créneaux de bar — sans afficher
                      une date de naissance dont personne n'a besoin ici. */}
                  {age !== null && (
                    <sup className="ml-1.5 align-super">
                      {/* Un badge coloré plutôt qu'un exposant gris : le gris
                          se confond avec le reste de la ligne, et l'âge sert
                          justement à distinguer d'un coup d'œil un mineur d'un
                          majeur — autorisation parentale, créneaux de bar. */}
                      <span
                        className={`inline-block rounded-full px-1.5 py-0.5 text-[0.6rem] font-bold leading-none ${
                          age < 18
                            ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
                            : 'bg-primary-100 text-primary-800 dark:bg-primary-900/40 dark:text-primary-200'
                        }`}
                      >
                        {age} ans
                      </span>
                    </sup>
                  )}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{user.email}</p>
                {user.telephone && (
                  <p className="text-sm text-gray-500 dark:text-gray-400">{user.telephone}</p>
                )}
                <div className="mt-1.5 flex flex-wrap items-center gap-2">
                  <RoleBadge roles={user.roles} showOnlyHighest={true} size="sm" />
                  {user.bureauRole && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
                      {user.bureauRole.label}
                    </span>
                  )}
                </div>
              </div>

              <button
                type="button"
                onClick={() => setEdition(true)}
                className="shrink-0 inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-primary-300 hover:text-primary-700"
              >
                <Pencil className="w-3.5 h-3.5" />
                Modifier
              </button>
            </>
          )}
        </div>

        {/* Dépliée : photo, téléphone et visibilité, tout au même endroit. */}
        {edition && (
          <div className="space-y-3 pt-3 border-t border-gray-100 dark:border-gray-800">
            <input
              className="input"
              type="tel"
              placeholder="Téléphone"
              value={formProfil.telephone}
              onChange={(e) => setFormProfil({ ...formProfil, telephone: e.target.value })}
            />

            <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={formProfil.anonyme}
                onChange={(e) => setFormProfil({ ...formProfil, anonyme: e.target.checked })}
                className="mt-0.5"
              />
              Ne pas apparaître sur la page publique « Association »
            </label>

            {/* Le consentement à la lettre d'information se donne et se
                retire ici, par la personne elle-même. Il est séparé du reste
                parce qu'il ne porte pas sur la même chose : les convocations
                et les rappels de créneau naissent de l'engagement pris, ils
                ne dépendent d'aucun accord et n'en demandent pas. */}
            <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={formProfil.newsletter}
                onChange={(e) => setFormProfil({ ...formProfil, newsletter: e.target.checked })}
                className="mt-0.5"
              />
              <span>
                Recevoir la lettre d'information de l'association
                <span className="block text-xs text-gray-500 dark:text-gray-400">
                  Sans rapport avec les convocations, les rappels de créneau et les messages du
                  bureau, qui vous parviennent de toute façon. Décochable à tout moment.
                </span>
              </span>
            </label>

            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => {
                  setNotice(null);
                  mutationProfil.mutate();
                }}
                disabled={mutationProfil.isPending}
                className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
              >
                {mutationProfil.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                Enregistrer
              </button>
              <button onClick={() => setEdition(false)} className="text-sm text-gray-500 dark:text-gray-400">
                Terminer
              </button>
            </div>
          </div>
        )}
      </div>


      {notice && (
        <div
          className={`card flex items-start gap-3 text-sm ${
            notice.type === 'success'
              ? 'bg-green-50 border-green-200 text-green-800'
              : 'bg-red-50 border-red-200 text-red-800'
          }`}
        >
          {notice.type === 'success' ? (
            <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0" />
          ) : (
            <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
          )}
          <p className="flex-1">{notice.message}</p>
          <button onClick={() => setNotice(null)} className="text-xs underline">Fermer</button>
        </div>
      )}

      {/* La fiche bénévole vivait sur l'écran des missions, où personne
          n'allait la chercher. Elle dit ce que le membre déclare de lui :
          sa place est ici. */}
      {estBenevole && <ProfilBenevole />}

      {/* Ne s'affichent que si le compte porte des devis ou des factures : un
          visiteur qui n'a jamais parrainé n'a rien à y voir. */}
      <MonSiteSponsor />
      <MesDevisSponsor />
      <MesFacturesSponsor />

      {/* Les raccourcis ont leur écran à eux. Le lien reste ici pour la barre
          du bas, qui n'a plus de place libre. Un sponsor n'y a pas sa place :
          tout ce qu'il ouvrirait lui serait refusé. */}
      {estBenevole && (
        <Link
          to="/acces-rapide"
          className="card flex items-center gap-3 hover:shadow-md transition lg:p-5"
        >
          <div className="p-2.5 rounded-xl bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 shrink-0">
            <LayoutGrid className="w-5 h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-medium text-gray-900 dark:text-gray-100">Accès rapide</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Scan des tickets, bénévoles, réunions et gestion.
            </p>
          </div>
          <ChevronRight className="w-5 h-5 text-gray-400 shrink-0" />
        </Link>
      )}

      {/* Les boîtes de fonction ont désormais leur écran, dans Mon espace →
          Messagerie : elles se consultent plusieurs fois par semaine, et les
          laisser ici obligeait à défiler devant les coordonnées et les
          missions pour un geste quotidien. */}

      {/* La protection du compte, juste avant les champs qui le modifient. */}
      <DoubleFacteur />

      {/*
        Les cles juste apres le code a six chiffres : ce sont deux reponses a la
        meme question, et les separer ferait choisir sans comparer.
      */}
      <MesClesSecurite />

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
        <div className="card lg:p-6 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <h2 className="font-semibold flex items-center gap-2">
              <Mail className="w-5 h-5 text-primary-600" /> Adresse e-mail personnelle
            </h2>
            <button
              type="button"
              onClick={() => setOuvertEmail(!ouvertEmail)}
              aria-expanded={ouvertEmail}
              aria-label={ouvertEmail ? 'Replier la modification d’e-mail' : 'Modifier mon e-mail'}
              className="shrink-0 p-1.5 rounded-lg text-gray-500 hover:text-primary-700 hover:bg-primary-50 dark:text-gray-400 dark:hover:bg-gray-700"
            >
              {ouvertEmail ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
          </div>

          {!ouvertEmail && (
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user.email}</p>
          )}

          {ouvertEmail && (
          <>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Votre e-mail sert d'identifiant de connexion. Sa modification est confirmée
            par votre mot de passe actuel.
          </p>

          <input
            className="input"
            type="email"
            placeholder="Nouvelle adresse e-mail"
            autoComplete="email"
            value={formEmail.email}
            onChange={(e) => setFormEmail({ ...formEmail, email: e.target.value })}
          />
          <input
            className="input"
            type="password"
            placeholder="Mot de passe actuel"
            autoComplete="current-password"
            value={formEmail.currentPassword}
            onChange={(e) => setFormEmail({ ...formEmail, currentPassword: e.target.value })}
          />

          <button
            onClick={() => {
              setNotice(null);
              mutationEmail.mutate();
            }}
            disabled={mutationEmail.isPending || emailInvalide}
            className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
          >
            {mutationEmail.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            Modifier mon e-mail
          </button>
          </>
          )}
        </div>

        <div className="card lg:p-6 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <h2 className="font-semibold flex items-center gap-2">
              <Lock className="w-5 h-5 text-primary-600" /> Mot de passe
            </h2>
            <button
              type="button"
              onClick={() => setOuvertMdp(!ouvertMdp)}
              aria-expanded={ouvertMdp}
              aria-label={ouvertMdp ? 'Replier le changement de mot de passe' : 'Changer mon mot de passe'}
              className="shrink-0 p-1.5 rounded-lg text-gray-500 hover:text-primary-700 hover:bg-primary-50 dark:text-gray-400 dark:hover:bg-gray-700"
            >
              {ouvertMdp ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
          </div>

          {!ouvertMdp && (
            <p className="text-xs text-gray-500 dark:text-gray-400">••••••••</p>
          )}

          {ouvertMdp && (
          <>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Au moins 8 caractères, dont une lettre et un chiffre.
          </p>

          <input
            className="input"
            type="password"
            placeholder="Mot de passe actuel"
            autoComplete="current-password"
            value={formMdp.currentPassword}
            onChange={(e) => setFormMdp({ ...formMdp, currentPassword: e.target.value })}
          />
          <div className="relative">
            <input
              className="input pr-10"
              type={visible ? 'text' : 'password'}
              placeholder="Nouveau mot de passe"
              autoComplete="new-password"
              value={formMdp.newPassword}
              onChange={(e) => setFormMdp({ ...formMdp, newPassword: e.target.value })}
            />
            <button
              type="button"
              onClick={() => setVisible(!visible)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              aria-label={visible ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
            >
              {visible ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          <input
            className="input"
            type={visible ? 'text' : 'password'}
            placeholder="Confirmez le nouveau mot de passe"
            autoComplete="new-password"
            value={formMdp.newPasswordConfirmation}
            onChange={(e) =>
              setFormMdp({ ...formMdp, newPasswordConfirmation: e.target.value })
            }
          />

          {formMdp.newPasswordConfirmation !== '' &&
            formMdp.newPassword !== formMdp.newPasswordConfirmation && (
              <p className="text-xs text-red-600">Les deux mots de passe ne correspondent pas.</p>
            )}

          <button
            onClick={() => {
              setNotice(null);
              mutationMdp.mutate();
            }}
            disabled={mutationMdp.isPending || mdpInvalide}
            className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
          >
            {mutationMdp.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            Modifier mon mot de passe
          </button>
          </>
          )}
        </div>
      </section>

      <button onClick={() => logout()} className="btn-outline w-full lg:w-auto">
        Se déconnecter
      </button>
    </div>
  );
}
